import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Package,
  Truck,
  Download,
  RotateCcw,
  MapPin,
  CreditCard,
  ExternalLink,
  Star,
  ChevronDown,
} from "lucide-react";
import { T } from "../../constants/theme";
import { formatDate, formatCurrency } from "../../utils/formatters";
import {
  resolveOrderStatus,
  resolveOrderStage,
  resolveOrderLabel,
  resolveOrderBadgeColor,
  resolveOrderItems,
  resolveOrderItemImage,
  resolveOrderTimeline,
  printOrderInvoice,
} from "../../utils/orderHelpers";
import { ORDER_STEPS } from "../../constants/orders";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "processing", label: "Processing" },
  { key: "confirmed", label: "Confirmed" },
  { key: "packed", label: "Packed" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
  { key: "cancelled", label: "Cancelled" },
  { key: "refunded", label: "Refunded" },
];

function matchesFilter(order, key) {
  const status = resolveOrderStatus(order);
  if (key === "all") return true;
  if (key === "processing") return ["pending", "processing"].includes(status);
  if (key === "confirmed") return status === "confirmed";
  if (key === "packed") return status === "packed";
  if (key === "shipped") return ["shipped", "out_for_delivery"].includes(status);
  if (key === "delivered") return status === "delivered";
  if (key === "cancelled") return status === "cancelled";
  if (key === "refunded") return ["refunded", "returned"].includes(status);
  return true;
}

function orderSearchText(order) {
  const items = resolveOrderItems(order);
  return [
    `order #${order.id}`,
    String(order.id),
    ...items.map((i) => i.name),
    ...items.map((i) => i.brand_name),
    formatDate(order.created_at),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function ThumbStack({ items }) {
  const shown = items.slice(0, 3);
  const remaining = items.length - shown.length;
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {shown.map((item, i) => (
        <img
          key={item.id || i}
          src={resolveOrderItemImage(item)}
          alt={item.name}
          style={{
            width: 56,
            height: 56,
            borderRadius: 12,
            objectFit: "cover",
            border: `1px solid ${T.border}`,
            background: T.surface,
            marginLeft: i === 0 ? 0 : -14,
            boxShadow: T.shadowSoft,
          }}
        />
      ))}
      {remaining > 0 && (
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 12,
            marginLeft: -14,
            background: T.ink,
            color: T.gold,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 800,
            border: `1px solid ${T.border}`,
          }}
        >
          +{remaining}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ order }) {
  const color = resolveOrderBadgeColor(order);
  return (
    <span
      style={{
        display: "inline-block",
        background: color + "15",
        color,
        border: `1px solid ${color}30`,
        borderRadius: 999,
        padding: "5px 12px",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: ".06em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {resolveOrderLabel(order)}
    </span>
  );
}

function SectionLabel({ children, icon: Icon }) {
  return (
    <div
      style={{
        fontSize: 12,
        color: T.muted,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".08em",
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {Icon ? <Icon size={13} /> : null}
      {children}
    </div>
  );
}

function SectionCard({ children, style }) {
  return (
    <div
      style={{
        background: T.surface,
        borderRadius: 16,
        padding: 18,
        border: `1px solid ${T.border}`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function OrderProductList({ items }) {
  if (!items.length) {
    return <div style={{ color: T.muted, fontSize: 14 }}>No item details available.</div>;
  }
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {items.map((item) => (
        <div
          key={item.id || `${item.product_id}-${item.name}`}
          style={{ display: "flex", gap: 12, borderBottom: `1px solid ${T.border}`, paddingBottom: 12 }}
        >
          <img
            src={resolveOrderItemImage(item)}
            alt={item.name}
            style={{ width: 64, height: 64, borderRadius: 12, objectFit: "cover", border: `1px solid ${T.border}`, background: T.surface }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: T.ink, fontWeight: 700, fontSize: 15 }}>{item.name}</div>
            {item.brand_name && (
              <div style={{ color: T.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: ".06em", marginTop: 2 }}>
                {item.brand_name}
              </div>
            )}
            <div style={{ color: T.muted, fontSize: 13, marginTop: 4 }}>
              Qty {item.qty} &middot; {formatCurrency(item.price)} each
            </div>
          </div>
          <div style={{ color: T.ink, fontWeight: 700, whiteSpace: "nowrap" }}>
            {formatCurrency(item.subtotal || item.price * item.qty)}
          </div>
        </div>
      ))}
    </div>
  );
}

function OrderSummary({ order }) {
  return (
    <SectionCard>
      <div style={{ display: "grid", gap: 8, fontSize: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", color: T.muted }}>
          <span>Subtotal</span>
          <span style={{ color: T.ink }}>{formatCurrency(order.subtotal)}</span>
        </div>
        {Number(order.discount) > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", color: T.muted }}>
            <span>Discount</span>
            <span style={{ color: T.ink }}>-{formatCurrency(order.discount)}</span>
          </div>
        )}
        {Number(order.shipping_cost) > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", color: T.muted }}>
            <span>Shipping</span>
            <span style={{ color: T.ink }}>{formatCurrency(order.shipping_cost)}</span>
          </div>
        )}
        {Number(order.tax) > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", color: T.muted }}>
            <span>Tax</span>
            <span style={{ color: T.ink }}>{formatCurrency(order.tax)}</span>
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            borderTop: `1px solid ${T.border}`,
            paddingTop: 8,
            marginTop: 4,
            fontWeight: 800,
            fontSize: 16,
            color: T.ink,
          }}
        >
          <span>Grand Total</span>
          <span>{formatCurrency(order.total)}</span>
        </div>
      </div>
    </SectionCard>
  );
}

function OrderTimeline({ order }) {
  const timeline = resolveOrderTimeline(order);
  return (
    <SectionCard>
      <div style={{ display: "grid", gap: 14 }}>
        {timeline.map((step) => (
          <div key={step.key} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: step.done ? T.gold : T.white,
                border: `2px solid ${step.done ? T.gold : T.border}`,
                flexShrink: 0,
              }}
            />
            <span style={{ color: step.done ? T.ink : T.muted, fontWeight: step.done ? 700 : 500, flex: 1 }}>
              {step.done ? "✓ " : "○ "}
              {step.label}
            </span>
            <span style={{ color: T.muted, fontSize: 12 }}>
              {step.date ? formatDate(step.date) : step.done ? "Reached" : "Pending"}
            </span>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function OrderTracking({ order }) {
  const hasTracking = Boolean(order.courier_name || order.tracking_number);
  if (!hasTracking) return null;
  return (
    <div>
      <SectionLabel icon={Package}>Tracking</SectionLabel>
      <SectionCard>
        <div style={{ display: "grid", gap: 10 }}>
          {order.courier_name && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: T.muted, fontSize: 13 }}>Courier</span>
              <span style={{ color: T.ink, fontWeight: 700 }}>{order.courier_name}</span>
            </div>
          )}
          {order.tracking_number && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: T.muted, fontSize: 13 }}>Tracking Number</span>
              <span style={{ color: T.ink, fontWeight: 700 }}>{order.tracking_number}</span>
            </div>
          )}
          {order.estimated_delivery && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: T.muted, fontSize: 13 }}>Estimated Delivery</span>
              <span style={{ color: T.ink, fontWeight: 700 }}>{formatDate(order.estimated_delivery)}</span>
            </div>
          )}
          {order.tracking_url && (
            <button
              onClick={() => window.open(order.tracking_url, "_blank", "noopener,noreferrer")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: T.ink,
                color: T.gold,
                border: "none",
                borderRadius: 12,
                padding: "11px 16px",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                marginTop: 4,
              }}
            >
              Track Package <ExternalLink size={14} />
            </button>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

function OrderActions({ order, items, onReorder, onReview }) {
  const status = resolveOrderStatus(order);
  const canReview = status === "delivered";

  const btnBase = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderRadius: 12,
    padding: "11px 18px",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  };

  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      <button
        onClick={() => printOrderInvoice(order)}
        style={{ ...btnBase, background: T.ink, color: T.gold, border: "none" }}
      >
        <Download size={15} /> Download Invoice
      </button>
      <button
        onClick={() => onReorder(items)}
        style={{ ...btnBase, background: T.white, color: T.ink, border: `1px solid ${T.gold}` }}
      >
        <RotateCcw size={15} /> Buy Again
      </button>
      {canReview && (
        <button
          onClick={() => onReview(items)}
          style={{ ...btnBase, background: T.white, color: T.ink, border: `1px solid ${T.border}` }}
        >
          <Star size={15} /> Leave Review
        </button>
      )}
    </div>
  );
}

function ExpandedOrderDetails({ order, onReorder, onReview }) {
  const items = resolveOrderItems(order);
  const hasAddress = Boolean(order.shipping_name || order.shipping_line1);

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{ overflow: "hidden" }}
    >
      <div
        style={{
          marginTop: 14,
          background: T.white,
          border: `1px solid ${T.gold}30`,
          borderRadius: 20,
          padding: 24,
        }}
      >
        <div style={{ marginBottom: 20 }}>
          <SectionLabel>Order Information</SectionLabel>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 14 }}>
            <div>
              <span style={{ color: T.muted }}>Order Number: </span>
              <span style={{ color: T.ink, fontWeight: 700 }}>#{order.id}</span>
            </div>
            <div>
              <span style={{ color: T.muted }}>Order Date: </span>
              <span style={{ color: T.ink, fontWeight: 700 }}>{formatDate(order.created_at)}</span>
            </div>
            <div>
              <span style={{ color: T.muted }}>Status: </span>
              <span style={{ color: T.ink, fontWeight: 700 }}>{resolveOrderLabel(order)}</span>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <SectionLabel>Purchased Items</SectionLabel>
          <OrderProductList items={items} />
        </div>

        {hasAddress && (
          <div style={{ marginBottom: 20 }}>
            <SectionLabel icon={MapPin}>Shipping Address</SectionLabel>
            <SectionCard>
              <div style={{ color: T.ink, fontWeight: 700 }}>{order.shipping_name}</div>
              <div style={{ color: T.muted, fontSize: 13, lineHeight: 1.6, marginTop: 4 }}>
                {order.shipping_line1}
                {order.shipping_line2 ? (
                  <>
                    <br />
                    {order.shipping_line2}
                  </>
                ) : null}
                <br />
                {[order.shipping_city, order.shipping_post].filter(Boolean).join(", ")}
                <br />
                {order.shipping_country}
              </div>
            </SectionCard>
          </div>
        )}

        {order.payment_method && (
          <div style={{ marginBottom: 20 }}>
            <SectionLabel icon={CreditCard}>Payment Information</SectionLabel>
            <SectionCard style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div style={{ color: T.ink, fontWeight: 700, textTransform: "capitalize" }}>
                {order.payment_method.replace(/_/g, " ")}
              </div>
              {order.payment_status && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    color: order.payment_status === "paid" ? "#2d6a4f" : T.gold,
                  }}
                >
                  {order.payment_status}
                </span>
              )}
              {order.transaction_id && (
                <div style={{ width: "100%", color: T.muted, fontSize: 12, marginTop: 4 }}>
                  Transaction ID: {order.transaction_id}
                </div>
              )}
            </SectionCard>
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <SectionLabel>Order Summary</SectionLabel>
          <OrderSummary order={order} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <SectionLabel icon={Truck}>Shipment Timeline</SectionLabel>
          <OrderTimeline order={order} />
        </div>

        <div style={{ marginBottom: 20 }}>
          <OrderTracking order={order} />
        </div>

        <OrderActions order={order} items={items} onReorder={onReorder} onReview={onReview} />
      </div>
    </motion.div>
  );
}

function OrderCard({ order, expanded, onToggle }) {
  const items = resolveOrderItems(order);
  const status = resolveOrderStatus(order);
  const cancelled = ["cancelled", "refunded", "returned"].includes(status);
  const stageIndex = resolveOrderStage(order);
  const primaryName = items[0]?.name || `Order #${order.id}`;
  const extraCount = items.length > 1 ? items.length - 1 : 0;

  return (
    <motion.div
      layout
      whileHover={{ y: -2 }}
      style={{
        background: expanded ? T.goldLight : T.white,
        border: expanded ? `1.5px solid ${T.gold}` : `1px solid ${T.border}`,
        borderRadius: 20,
        padding: 22,
        boxShadow: expanded ? "0 12px 30px rgba(196,154,60,.18)" : T.shadowSoft,
        transition: "box-shadow .3s ease, border-color .3s ease, background .3s ease",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <ThumbStack items={items} />
        <div style={{ textAlign: "right" }}>
          <StatusBadge order={order} />
          <div style={{ marginTop: 10, fontWeight: 800, fontSize: 18, color: T.ink }}>
            {formatCurrency(order.total)}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
          {primaryName}
          {extraCount > 0 ? ` +${extraCount} More Items` : ""}
        </div>
        <div style={{ marginTop: 6, color: T.muted, fontSize: 13 }}>
          Order #{order.id} &middot; Placed {formatDate(order.created_at)} &middot; {items.length}{" "}
          {items.length === 1 ? "Item" : "Items"}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${ORDER_STEPS.length}, 1fr)`, gap: 6, marginTop: 16 }}>
        {ORDER_STEPS.map((step, index) => {
          const active = !cancelled && stageIndex >= index;
          return (
            <div
              key={step}
              style={{
                height: 6,
                borderRadius: 999,
                background: active ? (step === "delivered" ? T.ink : T.gold) : T.border,
                transition: "background .3s ease",
              }}
            />
          );
        })}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <button
          onClick={() => onToggle(order.id)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "none",
            border: "none",
            color: T.gold,
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          {expanded ? "Hide Details" : "View Details"}
          <motion.span
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.25 }}
            style={{ display: "inline-flex" }}
          >
            <ChevronDown size={15} />
          </motion.span>
        </button>
      </div>
    </motion.div>
  );
}

function EmptyOrders({ onContinueShopping }) {
  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: 20,
        padding: "60px 24px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: T.surface,
          border: `1px solid ${T.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 44,
          margin: "0 auto 24px",
        }}
      >
        📦
      </div>
      <h3 style={{ margin: "0 0 8px", color: T.ink }}>No orders yet.</h3>
      <p style={{ color: T.muted, margin: "0 0 22px" }}>
        Your future purchases will appear here for easy tracking and management.
      </p>
      <button className="profile-cta" onClick={onContinueShopping}>
        Continue Shopping
      </button>
    </div>
  );
}

export default function OrdersManager({ orders = [], loading, navigate, addItem, toast }) {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  const safeOrders = useMemo(() => (Array.isArray(orders) ? orders : []), [orders]);

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return safeOrders.filter((order) => {
      if (!matchesFilter(order, activeFilter)) return false;
      if (!q) return true;
      return orderSearchText(order).includes(q);
    });
  }, [safeOrders, activeFilter, search]);

  const handleReorder = (items) => {
    if (!items.length) return;
    items.forEach((item) => {
      if (item.product_id) addItem(item.product_id, item.qty);
    });
    toast?.("Items added to your basket", "success");
  };

  const handleReview = (items) => {
    const target = items.find((i) => i.product_slug);
    if (target?.product_slug) {
      navigate(`/product/${target.product_slug}`);
    } else {
      toast?.("Product no longer available for review", "error");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          background: T.white,
          padding: 35,
          borderRadius: 24,
          border: `1px solid ${T.border}`,
          color: T.muted,
        }}
      >
        Loading orders...
      </div>
    );
  }

  if (safeOrders.length === 0) {
    return <EmptyOrders onContinueShopping={() => navigate("/shop")} />;
  }

  return (
    <div>
      <div style={{ position: "relative", marginBottom: 16 }}>
        <Search size={16} style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: T.muted }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by order number, product name, brand or date"
          style={{
            width: "100%",
            padding: "13px 16px 13px 42px",
            borderRadius: 12,
            border: `1px solid ${T.border}`,
            background: T.white,
            fontSize: 14,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 22 }}>
        {FILTERS.map((f) => {
          const active = activeFilter === f.key;
          return (
            <motion.button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.97 }}
              style={{
                background: active ? T.ink : T.surface,
                color: active ? T.gold : T.ink,
                border: `1px solid ${active ? T.ink : T.border}`,
                borderRadius: 999,
                padding: "9px 18px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                transition: "background .25s ease, color .25s ease",
              }}
            >
              {f.label}
            </motion.button>
          );
        })}
      </div>

      {filteredOrders.length === 0 ? (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 20, padding: 50, textAlign: "center", color: T.muted }}>
          No orders match your search or filter.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 18, maxHeight: 900, overflowY: "auto", paddingRight: 4 }}>
          {filteredOrders.map((order) => {
            const expanded = expandedId === order.id;
            return (
              <div key={order.id}>
                <OrderCard
                  order={order}
                  expanded={expanded}
                  onToggle={(id) => setExpandedId(expanded ? null : id)}
                />
                <AnimatePresence>
                  {expanded && (
                    <ExpandedOrderDetails order={order} onReorder={handleReorder} onReview={handleReview} />
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
